import { useEditorClass } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { CompleteEvent, EditorEvent, TunaPluginContentProps } from '@tunasong/plugin-lib'
import type { Comment, CoreElement, Entity, Persisted } from '@tunasong/schemas'
import { ScrollContainer, useActionToast } from '@tunasong/ui-lib'
import { useCurrentUser } from '@tunasong/redux'
import { logger } from '@tunasong/models'
import type { FC, ReactNode } from 'react'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { Composer } from './composer.js'
import type { ComposerProps, ComposerSendOptions } from './composer.js'
import ChatFeed from './chat-feed.js'
import type { ChatFeedEntry, ChatFeedStandalone } from './chat-feed.js'
import { isComposerEmpty } from './composer-empty.js'
import { useChat } from './chat.hook.js'
import { ChatFilterGroup } from './chat-filter.js'
import type { ChatFilter } from './chat-filter.js'
import { ChatToastContext } from './chat-toast.js'
import { CommentContent } from './comment-content.js'
import { CommentMenu } from './comment-menu.js'
import { shouldPatchComment } from './should-patch-comment.js'
import { TaskList } from './task-list.js'
import type { TaskEntry } from './task-list.js'
import { useEntityTasks } from './use-entity-tasks.js'
import cn from 'classnames'
import styles from './chat.module.css'

const ChatItemStyles = {
  fontSize: '14px !important',
  justifyContent: 'center',
}

export interface ComposerSeed {
  /** Text to hand to the composer. */
  text: string
  /** Send immediately as a comment instead of pre-filling. */
  autoSend?: boolean
  /** Monotonic hand-off id — a new hand-off always has a new seq. */
  seq: number
}

export interface ChatGroup {
  id: string
  /** ISO time of the group's latest comment */
  latestActivity: string
  content: ReactNode
}

export interface ChatProps extends TunaPluginContentProps<Entity> {
  className?: string
  /** One-shot composer hand-off (pre-fill or auto-send) — see ComposerSeed. */
  composerSeed?: ComposerSeed
  /** Stable id for the composer root (aria-describedby wiring by siblings). */
  composerId?: string
  /**
   * How the composer reads and what it offers, for the agents: their placeholder (C2), the answer state with Stop,
   * the answering label (C8), a notice and the focus chip (chat makeover #2718). People Chat uses the defaults.
   */
  composerProps?: Pick<
    ComposerProps,
    'placeholder' | 'hint' | 'notice' | 'state' | 'streamingLabel' | 'onStop' | 'focusChip' | 'autoFocus'
  >
  /** Shown in place of the feed once the comments have loaded and none are open. */
  emptyState?: ReactNode
  /**
   * Replaces the comment feed, keeping the composer and its sending: the Agents panel's thread (#2677 M4). It can
   * show the chat's toast through `useChatToast`. With a feed, `emptyState` and `groups` are not shown.
   */
  feed?: ReactNode
  /**
   * Content placed among the comments by its latest activity, newest next to the composer: the song's anchored
   * groups in the sidebar Chat (chat makeover M3, #2718). It can show the chat's toast through `useChatToast`.
   */
  groups?: ChatGroup[]
  /** Merged into each comment this chat sends: the block thread's anchor (GH #2795) */
  commentMetadata?: Record<string, unknown>
  /**
   * People Chat (GH #3041): the composer's Task toggle (T1), and on each row the task checkbox and the ⋮ menu. The
   * agents' chat never passes it, so its comments are never tasks.
   */
  taskToggle?: boolean
  /**
   * The filter at the top (chat tasks M2.4, GH #3041; T8–T10), shown with two or more and never with a `feed`. The
   * owner holds the choice, `filter` and `onFilterChange`, so the full page can keep it in its URL.
   */
  filters?: ChatFilter[]
  filter?: ChatFilter
  /** Where the composer keeps its draft, `${entityId}:${threadId ?? ''}` (chat tasks M5.4) */
  draftKey?: string
  /** For Tasks, beyond this chat's own: the song's anchored tasks */
  tasks?: TaskEntry[]
  /** What Resolved shows: the song's resolved threads */
  resolvedContent?: ReactNode
  onFilterChange?(filter: ChatFilter): void
  /** Scrolling to the top asks for older groups too */
  onLoadOlder?(): void
  onComment?(comment: Comment): void
}

export const Chat: FC<ChatProps> = props => {
  const {
    element,
    className,
    onComment,
    composerSeed,
    composerId,
    emptyState,
    feed: customFeed,
    composerProps,
    groups = [],
    onLoadOlder,
    commentMetadata,
    taskToggle = false,
    filters = [],
    filter = 'all',
    onFilterChange,
    tasks = [],
    resolvedContent,
    draftKey,
  } = props
  const updateEntity = graphHooks.useEntityUpdate()

  const ChatEditor = useEditorClass('ChatEditor')

  const { userId } = useCurrentUser()

  const {
    comments: chatComments,
    addComment,
    getNext,
    isLoading,
  } = useChat({
    element,
    pageSize: 10,
    commentMetadata,
  })
  /** The chat's one toast; anchored groups show theirs here too (`useChatToast`) */
  const { showToast, toast } = useActionToast()

  /** A failed send keeps its text and offers Retry (GH #2453; Joni's copy). One toast at a time, newest wins. */
  const showSendFailed = (retry: () => void, { restored }: { restored: boolean }) =>
    showToast({
      message: restored ? "Couldn't send your comment. It's back in the box." : "Couldn't send your comment.",
      actionLabel: 'Retry',
      onAction: retry,
      severity: 'error',
      /** With the text back in the box, Send is a second way back; otherwise Retry is the only one, so it stays */
      duration: restored ? undefined : null,
    })
  /** The auto-send effect must not re-run because the toast's callbacks change identity */
  const handleAutoSendFailed = useEffectEvent((retry: () => void) => showSendFailed(retry, { restored: false }))

  const commentTemplate = element?.id
    ? ({
        type: 'comment',
        parentId: element.id,
        userId: userId ?? 'unknown',
        children: [{ type: 'line', children: [{ text: '' }] }],
      } satisfies CoreElement<Comment>)
    : null

  const commentRef = useRef<Comment | null>(commentTemplate)

  /**
   * Auto-send hand-off: post `composerSeed.text` as a comment immediately and
   * fire `onComment`. Keyed on `seq` so each hand-off fires exactly once even
   * though `commentTemplate`/`addComment` change identity across renders.
   */
  const autoSubmittedSeq = useRef(0)
  useEffect(() => {
    if (!composerSeed?.autoSend || composerSeed.text.length === 0 || !commentTemplate) {
      return
    }
    if (autoSubmittedSeq.current === composerSeed.seq) {
      return
    }
    autoSubmittedSeq.current = composerSeed.seq
    const comment: Comment = {
      ...commentTemplate,
      children: [{ type: 'line', children: [{ text: composerSeed.text }] }],
    }
    const send = async () => {
      try {
        const created = await addComment(comment)
        if (onComment && created) {
          onComment(created)
        }
      } catch (error) {
        logger.warn('Comment send failed', { parentId: comment.parentId, error })
        handleAutoSendFailed(() => void send())
      }
    }
    void send()
  }, [composerSeed, commentTemplate, addComment, onComment])

  /**
   * GH #1280: gate PATCH on a real content edit. Plate's onValueChange
   * fires on initial normalization and on every prop-driven editor
   * re-create, which previously produced a phantom PATCH on every
   * rendered comment — and a 404-racing PATCH against the just-POSTed
   * comment. Updates may legitimately occur even when the editor is
   * readOnly (e.g. todo checkboxes nested inside the comment), so the
   * decision is content-based, not readOnly-based.
   */
  const handleCommentUpdate = (original: Persisted<Comment>) => (e: CoreElement) => {
    if (!shouldPatchComment(original, e)) {
      return
    }
    updateEntity(e.id, e)
  }

  const createFeedItems = (feedComments: Persisted<Comment>[] = []) =>
    feedComments?.map(c => {
      /* @todo this will flicker when receiving updates. Can we render this offline? */
      const body = (
        <ChatEditor
          style={ChatItemStyles}
          readOnly={true}
          autoFocus={false}
          element={c}
          onChange={handleCommentUpdate(c)}
        />
      )
      return {
        ...c,
        content: (
          <div className={styles.feedItemContent}>
            {taskToggle ? <CommentContent comment={c}>{body}</CommentContent> : body}
          </div>
        ),
        actions: taskToggle ? <CommentMenu comment={c} /> : undefined,
      }
    }) ?? []

  /**
   * comment state is set to the comment object
   */
  /** We keep a counter, so that we ignore pending comments when the expected count is at what we expect */
  const expectedCommentCount = useRef(0)

  /** Send a comment that failed while the box held something else; the box is never touched */
  const sendFailedComment = async (failed: Comment, options: ComposerSendOptions) => {
    try {
      const created = await addComment(failed, options)
      if (onComment && created) {
        onComment(created)
      }
    } catch (error) {
      logger.warn('Comment send failed', { parentId: failed.parentId, error })
      showSendFailed(() => void sendFailedComment(failed, options), { restored: false })
    }
  }

  /** Resolves false when the send failed, so the composer puts the text back (GH #2453) */
  const handleAddComment = async (
    ev: EditorEvent,
    retry: () => Promise<void>,
    options: ComposerSendOptions = { task: false }
  ): Promise<boolean> => {
    ev.preventDefault()
    invariant(commentRef.current, 'Comment should be set')
    /**
     * The comment is what the box held at Send. The copy `onChange` last reported can lag it: a draft the box was
     * restored with is never reported, so it went out empty and showed as a blank comment (GH #3146).
     */
    const comment = options.body ? { ...commentRef.current, children: options.body } : commentRef.current
    const sendOptions = { task: options.task }

    expectedCommentCount.current = chatComments.length + 1

    /** @todo The problem here is that the update from server side comes before this code is executed */
    let createdComment: Persisted<Comment> | undefined
    try {
      createdComment = await addComment(comment, sendOptions)
    } catch (error) {
      logger.warn('Comment send failed', { parentId: comment.parentId, error })
      const { editor } = (ev.detail ?? {}) as Partial<CompleteEvent>
      const restored = Boolean(editor && isComposerEmpty(editor))
      /**
       * Restored: the box holds this comment again, so Retry submits the box. Not restored: the box holds a new
       * draft, so Retry must send exactly the comment that failed and leave the draft alone (GH #2453, Joni's P1).
       */
      showSendFailed(restored ? () => void retry() : () => void sendFailedComment(comment, sendOptions), { restored })
      return false
    }

    if (onComment && createdComment) {
      onComment(createdComment)
    }
    commentRef.current = commentTemplate
    return true
  }

  const comments = createFeedItems(chatComments)
  /** Groups sit among the comments by their latest activity (chat makeover M3); with none, the order is unchanged */
  const feed: ChatFeedEntry[] =
    groups.length === 0
      ? comments
      : [
          ...comments.map(item => ({ item, at: item.createdAt })),
          ...groups.map(group => ({
            item: {
              id: group.id,
              createdAt: group.latestActivity,
              content: group.content,
              standalone: true as const,
            } satisfies ChatFeedStandalone,
            at: group.latestActivity,
          })),
        ]
          .sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''))
          .map(({ item }) => item)

  const handleComment = (c: Comment) => (commentRef.current = c)

  const handleUp = async () => {
    onLoadOlder?.()
    await getNext()
  }

  // we use state to trigger a re-render since we need topRef to be set for ScrollContainer
  const [topEl, setTopEl] = useState<HTMLDivElement | null>(null)

  const showFilter = !customFeed && filters.length > 1
  const view = showFilter ? filter : 'all'
  const ownTasks = useEntityTasks(showFilter ? element?.id : undefined)

  return (
    /** The chat's root: a resolved group moves focus to the next one, or to this composer (chat tasks M3) */
    <div className={cn(styles.container, className)} data-chat-root="">
      <>
        {showFilter ? <ChatFilterGroup filters={filters} value={filter} onChange={f => onFilterChange?.(f)} /> : null}
        {view === 'tasks' ? (
          <ScrollContainer onUp={handleUp} topEl={null}>
            <ChatToastContext value={showToast}>
              <TaskList tasks={[...ownTasks, ...tasks]} />
            </ChatToastContext>
          </ScrollContainer>
        ) : null}
        {view === 'resolved' ? (
          <ScrollContainer topEl={null}>
            <ChatToastContext value={showToast}>{resolvedContent}</ChatToastContext>
          </ScrollContainer>
        ) : null}
        {view !== 'all' ? null : !customFeed && emptyState && !isLoading && feed.length === 0 ? (
          <div className={styles.empty}>{emptyState}</div>
        ) : (
          <ScrollContainer onUp={handleUp} topEl={topEl}>
            <ChatToastContext value={showToast}>
              {customFeed ?? <ChatFeed items={feed} topRef={setTopEl} />}
            </ChatToastContext>
          </ScrollContainer>
        )}
        {commentRef.current ? (
          <Composer
            /** C12; the agents pass their own (C2) */
            placeholder="Write a comment…"
            {...composerProps}
            onChange={handleComment}
            onComplete={handleAddComment}
            taskToggle={taskToggle}
            draftKey={draftKey}
            seed={
              composerSeed && !composerSeed.autoSend ? { text: composerSeed.text, seq: composerSeed.seq } : undefined
            }
            id={composerId}
            aria-describedby={composerId ? `${composerId}-role-description` : undefined}
          />
        ) : null}
      </>
      {toast}
    </div>
  )
}
